import React, { useState, useMemo } from 'react';

// MUI Components
import { Paper, Box, Tooltip } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';

// Project Components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import MDProgress from 'components/MDProgress';

// Search Context
import { useSearch } from 'layouts/production/Pages/SearchProvider';

// Components
import { ProductionResponsibles } from './components/responsibles';
import PinProduction from './components/pinProduction';
import FastEdit from './components/fastEdit';

// Status color mapping for badges
const getStatusBadgeProps = (isActive, aaroStatus) => {
    if (!isActive) {
        return { color: 'error', variant: 'contained' };
    }

    if (aaroStatus) {
        return { color: 'success', variant: 'contained' };
    }

    return { color: 'warning', variant: 'contained' };
};

// Helper function to get display text from status
const getStatusDisplayText = (isActive, aaroStatus) => {
    if (!isActive) return 'Pasif';
    if (aaroStatus) return 'Aktif';
    return 'Beklemede';
};

// Helper function to format currency
const formatCurrency = (amount, symbol = '₺') => {
    if (!amount) return '0,00 ' + symbol;
    return (
        new Intl.NumberFormat('tr-TR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount) +
        ' ' +
        symbol
    );
};

// Helper function to format date
const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

// Helper function to get status group info
const getStatusGroupInfo = (status) => {
    if (!status || !status.name) {
        return {
            name: 'Belirtilmemiş',
            color: '#9e9e9e',
            order: 0,
        };
    }

    return {
        name: status.name,
        color: status.color || '#2196f3',
        order: status.order || 999,
    };
};

// Custom Toolbar Component
function CustomToolbar(props) {
    return (
        <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={2}
            borderBottom="1px solid"
            borderColor="grey.200"
            backgroundColor="white"
        >
            <MDTypography variant="h6" color="dark" fontWeight="medium">
                Üretim Yönetimi
            </MDTypography>
        </MDBox>
    );
}

// Main Component
export default function ProductionsData() {
    const { data: all, page, updatePage, limit, updateLimit, queryInfo } = useSearch();
    const [selectedRows, setSelectedRows] = useState([]);

    const data = all?.result || [];
    const totalCount = all?.totalCount || 0;
    const user = JSON.parse(localStorage.getItem('user'));

    // Transform data into tree structure
    const treeData = useMemo(() => {
        if (!data || data.length === 0) {
            return [];
        }

        // Group data by status
        const groupedData = {};

        data.forEach((item) => {
            const statusInfo = getStatusGroupInfo(item.status);
            const statusKey = statusInfo.name;

            if (!groupedData[statusKey]) {
                groupedData[statusKey] = {
                    items: [],
                    statusInfo: statusInfo,
                };
            }

            groupedData[statusKey].items.push(item);
        });

        // Create tree structure
        const treeRows = [];
        let rowId = 1;

        // Sort status groups by order (Belirtilmemiş first, then by order)
        const sortedStatusKeys = Object.keys(groupedData).sort((a, b) => {
            const aInfo = groupedData[a].statusInfo;
            const bInfo = groupedData[b].statusInfo;
            return aInfo.order - bInfo.order;
        });

        sortedStatusKeys.forEach((statusKey) => {
            const group = groupedData[statusKey];
            const statusInfo = group.statusInfo;

            // Add parent row (status group)
            const parentId = `status-${rowId++}`;
            treeRows.push({
                id: parentId,
                hierarchy: [statusKey],
                customer: statusKey,
                status: null,
                amount: null,
                progress: null,
                responsibles: null,
                dates: null,
                actions: null,
                isStatusGroup: true,
                statusInfo: statusInfo,
                itemCount: group.items.length,
                totalAmount: group.items.reduce((sum, item) => sum + (item.aaroFields?.Tutar || 0), 0),
            });

            // Add child rows (individual productions)
            group.items.forEach((item) => {
                treeRows.push({
                    id: item._id,
                    hierarchy: [statusKey, `${item.aaroCustomerName}-${item._id}`],
                    customer: item.aaroCustomerName,
                    status: item.status,
                    amount: item.aaroFields?.Tutar,
                    progress: item.taskList?.completionPercentage,
                    responsibles: item.responsibles,
                    dates: item.createdAt,
                    actions: item._id,
                    isStatusGroup: false,
                    ...item,
                });
            });
        });

        return treeRows;
    }, [data]);

    if (queryInfo.isError) {
        return (
            <MDBox p={3}>
                <MDTypography variant="h6" color="error">
                    Hata Oluştu
                </MDTypography>
                <MDTypography variant="body2" color="text">
                    {JSON.stringify(queryInfo.error)}
                </MDTypography>
            </MDBox>
        );
    }

    if (queryInfo.isLoading) {
        return (
            <MDBox p={3} textAlign="center">
                <MDTypography variant="body1" color="text">
                    Üretim verileri yükleniyor...
                </MDTypography>
            </MDBox>
        );
    }

    // DataGrid sütunları
    const getColumns = () => [
        {
            field: 'status',
            headerName: 'Durum',
            flex: 0.8,
            minWidth: 80,
            renderCell: (params) => {
                if (params.row.isStatusGroup) {
                    return null;
                }

                const badgeProps = getStatusBadgeProps(params.row.isActive, params.row.aaroFields?.Durum);
                return (
                    <MDBox display="flex" alignItems="center" justifyContent="center" height="100%">
                        <MDBadge
                            {...badgeProps}
                            size="xs"
                            badgeContent={getStatusDisplayText(params.row.isActive, params.row.aaroFields?.Durum)}
                        />
                    </MDBox>
                );
            },
        },
        {
            field: 'amount',
            headerName: 'Tutar',
            flex: 1.2,
            minWidth: 100,
            renderCell: (params) => {
                if (params.row.isStatusGroup) {
                    return (
                        <MDBox display="flex" alignItems="center" height="100%">
                            <MDTypography variant="caption" fontWeight="bold" color="dark">
                                {formatCurrency(params.row.totalAmount)}
                            </MDTypography>
                        </MDBox>
                    );
                }

                return (
                    <MDBox display="flex" alignItems="center" height="100%">
                        <MDTypography variant="caption" fontWeight="bold" color="text.primary">
                            {formatCurrency(params.row.aaroFields?.Tutar, params.row.aaroFields?.DovizSembol)}
                        </MDTypography>
                    </MDBox>
                );
            },
        },
        {
            field: 'progress',
            headerName: 'İlerleme',
            flex: 0.8,
            minWidth: 70,
            renderCell: (params) => {
                if (params.row.isStatusGroup) {
                    const avgProgress =
                        params.row.itemCount > 0
                            ? Math.round(
                                  data
                                      .filter(
                                          (item) => getStatusGroupInfo(item.status).name === params.row.statusInfo.name
                                      )
                                      .reduce((sum, item) => sum + (item.taskList?.completionPercentage || 0), 0) /
                                      params.row.itemCount
                              )
                            : 0;

                    return (
                        <MDBox display="flex" alignItems="center" height="100%">
                            <MDTypography variant="caption" fontWeight="bold" color="text.primary">
                                %{avgProgress}
                            </MDTypography>
                        </MDBox>
                    );
                }

                const taskCompletion = params.row.taskList?.completionPercentage || 0;

                return (
                    <MDBox display="flex" alignItems="center" height="100%">
                        <MDTypography variant="caption" fontWeight="bold" color="text.primary">
                            %{taskCompletion}
                        </MDTypography>
                    </MDBox>
                );
            },
        },
        {
            field: 'responsibles',
            headerName: 'Sorumlular',
            flex: 1,
            minWidth: 80,
            renderCell: (params) => {
                if (params.row.isStatusGroup) {
                    return null;
                }

                return (
                    <MDBox display="flex" alignItems="center" height="100%">
                        <ProductionResponsibles responsibles={params.value} />
                    </MDBox>
                );
            },
        },
        {
            field: 'dates',
            headerName: 'Tarihler',
            flex: 1,
            minWidth: 90,
            renderCell: (params) => {
                if (params.row.isStatusGroup) {
                    return null;
                }

                return (
                    <MDBox display="flex" alignItems="center" height="100%">
                        <MDTypography variant="caption" fontWeight="bold" color="text.primary">
                            {formatDate(params.row.createdAt)}
                        </MDTypography>
                    </MDBox>
                );
            },
        },
        {
            field: 'actions',
            headerName: 'İşlemler',
            width: 70,
            sortable: false,
            renderCell: (params) => {
                if (params.row.isStatusGroup) {
                    return null;
                }

                return (
                    <MDBox display="flex" gap={0.5} justifyContent="center" alignItems="center" height="100%">
                        <Tooltip
                            title={params.row?.pinnedBy?.includes(user?.userID) ? 'Sabitlemeyi Kaldır' : 'Sabitle'}
                            arrow
                        >
                            <Box>
                                <PinProduction
                                    isPinned={params.row?.pinnedBy?.includes(user?.userID)}
                                    productionID={params.row._id}
                                    productions={data}
                                    userID={user?.userID}
                                />
                            </Box>
                        </Tooltip>
                        <Tooltip title="Hızlı Düzenle" arrow>
                            <Box>
                                <FastEdit aaroOrderID={params.row.aaroOrder} />
                            </Box>
                        </Tooltip>
                    </MDBox>
                );
            },
        },
    ];

    return (
        <MDBox opacity={1}>
            <Paper
                elevation={0}
                sx={{
                    width: '100%',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    '& .MuiDataGrid-root': {
                        border: 'none',
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                        fontSize: '0.8rem',
                        fontWeight: '400',
                        backgroundColor: 'white',
                    },
                    '& .MuiDataGrid-cell': {
                        borderColor: 'grey.200',
                        borderWidth: '0 0 1px 0',
                        padding: '2px 6px',
                        fontSize: '0.7rem',
                        fontWeight: '400',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        height: '28px',
                        minHeight: '28px',
                        maxHeight: '28px',
                        '&[data-field="status"]': {
                            justifyContent: 'center',
                        },
                        '&[data-field="actions"]': {
                            justifyContent: 'center',
                        },
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: 'grey.50',
                        color: 'text.primary',
                        fontWeight: '500',
                        fontSize: '0.65rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.025em',
                        borderBottom: '1px solid',
                        borderColor: 'grey.200',
                        minHeight: '32px !important',
                        '& .MuiDataGrid-columnHeader': {
                            padding: '0 6px',
                            outline: 'none',
                            '&:focus': {
                                outline: 'none',
                            },
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: '500',
                            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                            fontSize: '0.65rem',
                            color: 'text.primary',
                        },
                        '& .MuiDataGrid-iconSeparator': {
                            display: 'none',
                        },
                    },
                    '& .MuiDataGrid-virtualScroller': {
                        backgroundColor: 'white',
                    },
                    '& .MuiDataGrid-footerContainer': {
                        backgroundColor: 'white',
                        borderTop: '1px solid',
                        borderColor: 'grey.200',
                        minHeight: '44px',
                        padding: '8px 12px',
                        '& .MuiTablePagination-root': {
                            color: 'text.secondary',
                            fontFamily: '"Inter", sans-serif',
                            fontSize: '0.875rem',
                        },
                        '& .MuiTablePagination-select': {
                            fontFamily: '"Inter", sans-serif',
                            fontSize: '0.875rem',
                        },
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                            fontSize: '0.8rem',
                            color: 'text.secondary',
                        },
                    },
                    '& .MuiDataGrid-row': {
                        height: '28px !important',
                        minHeight: '28px !important',
                        maxHeight: '28px !important',
                        borderBottom: '1px solid',
                        borderColor: 'grey.200',
                        '&:hover': {
                            backgroundColor: 'grey.50',
                        },
                        '&.Mui-selected': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08) !important',
                        },
                        '&.inactiveRow': {
                            opacity: 0.6,
                        },
                        '&.statusGroupRow': {
                            backgroundColor: 'grey.50',
                        },
                    },
                    '& .MuiDataGrid-cell:focus': {
                        outline: 'none',
                    },
                    '& .MuiDataGrid-columnHeader:focus': {
                        outline: 'none',
                    },
                    '& .MuiCheckbox-root': {
                        color: 'text.secondary',
                        '&.Mui-checked': {
                            color: 'primary.main',
                        },
                    },
                    '& .MuiDataGrid-toolbarContainer': {
                        padding: '0',
                        border: 'none',
                        '& .MuiButton-root': {
                            color: 'text.primary',
                            fontWeight: '400',
                            fontFamily: '"Inter", sans-serif',
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            '&:hover': {
                                backgroundColor: 'grey.100',
                            },
                        },
                        '& .MuiInputBase-root': {
                            fontFamily: '"Inter", sans-serif',
                            fontSize: '0.875rem',
                            borderRadius: '4px',
                        },
                        '& .MuiTextField-root': {
                            '& .MuiInputBase-root': {
                                height: '32px',
                                fontSize: '0.875rem',
                            },
                        },
                    },
                    // Tree view specific styles
                    '& .MuiDataGrid-treeDataGroupingCell': {
                        paddingLeft: '8px !important',
                    },
                    '& .MuiDataGrid-treeDataGroupingCellToggle': {
                        marginRight: '4px',
                    },
                    // Responsive design
                    '@media (max-width: 1200px)': {
                        '& .MuiDataGrid-columnHeader--progress': {
                            display: 'none',
                        },
                        '& .MuiDataGrid-cell--progress': {
                            display: 'none',
                        },
                    },
                    '@media (max-width: 900px)': {
                        '& .MuiDataGrid-columnHeader--responsibles': {
                            display: 'none',
                        },
                        '& .MuiDataGrid-cell--responsibles': {
                            display: 'none',
                        },
                        '& .MuiDataGrid-columnHeader--dates': {
                            display: 'none',
                        },
                        '& .MuiDataGrid-cell--dates': {
                            display: 'none',
                        },
                    },
                    '@media (max-width: 768px)': {
                        '& .MuiDataGrid-columnHeader--amount': {
                            display: 'none',
                        },
                        '& .MuiDataGrid-cell--amount': {
                            display: 'none',
                        },
                        '& .MuiDataGrid-cell': {
                            padding: '6px 8px',
                            fontSize: '0.8rem',
                            height: '48px !important',
                            minHeight: '48px !important',
                            maxHeight: '48px !important',
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            minHeight: '36px !important',
                        },
                        '& .MuiDataGrid-row': {
                            height: '48px !important',
                            minHeight: '48px !important',
                            maxHeight: '48px !important',
                        },
                    },
                }}
            >
                <DataGridPro
                    rows={treeData}
                    columns={getColumns()}
                    treeData
                    getTreeDataPath={(row) => row.hierarchy}
                    groupingColDef={{
                        headerName: 'GROUP',
                        width: 300,
                    }}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: limit,
                                page: page - 1,
                            },
                        },
                        sorting: {
                            sortModel: [{ field: 'createdAt', sort: 'desc' }],
                        },
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    checkboxSelection
                    disableRowSelectionOnClick
                    onRowSelectionModelChange={(newSelection) => {
                        setSelectedRows(newSelection);
                    }}
                    slots={{
                        toolbar: CustomToolbar,
                    }}
                    loading={queryInfo.isLoading}
                    rowHeight={28}
                    density="compact"
                    paginationMode="server"
                    rowCount={totalCount}
                    paginationModel={{
                        page: page - 1,
                        pageSize: limit,
                    }}
                    onPaginationModelChange={(model) => {
                        updatePage(model.page + 1);
                        updateLimit(model.pageSize);
                    }}
                    getRowClassName={(params) => {
                        if (params.row.isStatusGroup) {
                            return 'statusGroupRow';
                        }
                        return params.row.isActive ? '' : 'inactiveRow';
                    }}
                    sx={{
                        height: 'calc(100vh - 200px)',
                        minHeight: '500px',
                    }}
                />
            </Paper>
        </MDBox>
    );
}
